import axios from 'axios';
import { API_BASE_URL } from '../config/config';

/**
 * OTP Service
 * Handles OTP generation and validation using the new API endpoints
 */
class OTPService {
  private apiClient = axios.create({
    baseURL: API_BASE_URL,
    timeout: 30000,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  /**
   * Generate OTP and send via SMS
   * POST: /api/OTP/generate
   */
  async generateOTP(customerCode: string, phoneNumber: string): Promise<{
    success: boolean;
    message: string;
    otpCode?: string; // Only for development/testing
    expiryDateTime?: string;
  }> {
    try {
      const response = await this.apiClient.post('/OTP/generate', {
        customerCode,
        phoneNumber
      });

      if (response.data?.success || response.data?.Success) {
        return {
          success: true,
          message: response.data.message || response.data.Message || 'OTP sent successfully',
          otpCode: response.data.otpCode || response.data.OTPCode, // For testing only
          expiryDateTime: response.data.expiryDateTime || response.data.ExpiryDateTime
        };
      }

      return {
        success: false,
        message: response.data?.message || response.data?.Message || 'Failed to generate OTP'
      };
    } catch (error: any) {
      console.error('❌ Error generating OTP:', error);

      // Extract error message from response
      const errorMessage = error.response?.data?.message ||
                          error.response?.data?.Message ||
                          error.message ||
                          'Failed to send OTP. Please try again.';

      return {
        success: false,
        message: errorMessage
      };
    }
  }

  /**
   * Validate OTP
   * POST: /api/OTP/validate
   */
  async validateOTP(customerCode: string, phoneNumber: string, otpCode: string): Promise<{
    success: boolean;
    message: string;
    isValid: boolean;
    status?: string;
  }> {
    try {
      const response = await this.apiClient.post('/OTP/validate', {
        customerCode,
        phoneNumber,
        otpCode
      });

      if (response.data?.success || response.data?.Success) {
        return {
          success: true,
          message: response.data.message || response.data.Message || 'OTP validated successfully',
          isValid: response.data.isValid || response.data.IsValid || false,
          status: response.data.status || response.data.Status
        };
      }

      return {
        success: false,
        message: response.data?.message || response.data?.Message || 'Invalid OTP',
        isValid: false,
        status: response.data?.status || response.data?.Status
      };
    } catch (error: any) {
      console.error('❌ Error validating OTP:', error);

      // Extract error message from response
      const errorMessage = error.response?.data?.message ||
                          error.response?.data?.Message ||
                          error.message ||
                          'Failed to validate OTP. Please try again.';

      return {
        success: false,
        message: errorMessage,
        isValid: false
      };
    }
  }

  /**
   * Check OTP status (for debugging)
   * GET: /api/OTP/status/{customerCode}/{phoneNumber}
   */
  async getOTPStatus(customerCode: string, phoneNumber: string): Promise<{
    success: boolean;
    message?: string;
    records?: any[];
  }> {
    try {
      const response = await this.apiClient.get(`/OTP/status/${customerCode}/${phoneNumber}`);

      if (response.data?.success || response.data?.Success) {
        return {
          success: true,
          records: response.data.records || response.data.Records || []
        };
      }

      return {
        success: false,
        message: response.data?.message || response.data?.Message || 'Failed to get OTP status'
      };
    } catch (error: any) {
      console.error('❌ Error getting OTP status:', error);

      return {
        success: false,
        message: error.message || 'Failed to get OTP status'
      };
    }
  }
}

// Export singleton instance
const otpService = new OTPService();
export default otpService;

// Export class for testing
export { OTPService };
